import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import invoiceGenerator from '../../services/invoiceGenerator';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import apiService from '../../services/api';
import { usePermissionContext } from '../../contexts/PermissionContext';
import ViewPurchaseModal, { printPurchaseOrder } from './ViewPurchaseModal';
import EditShippingModal from './EditShippingModal';
import SendNotificationModal from './SendNotificationModal';

const PurchaseOrder = () => {
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission, isSuperAdmin } = usePermissionContext();

  const canView = isSuperAdmin || hasPermission('purchase_order.view_all') || hasPermission('purchase_order.view_own');
  const canEdit = isSuperAdmin || hasPermission('purchase_order.update');
  const canDelete = isSuperAdmin || hasPermission('purchase_order.delete');
  const canEditShipping = isSuperAdmin || hasAnyPermission(['access_shipping', 'access_own_shipping', 'access_commission_agent_shipping']);

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);
  const [error, setError] = useState(null);

  const [selectedPurchaseForView, setSelectedPurchaseForView] = useState(null);
  const [isLoadingViewModal, setIsLoadingViewModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPurchaseForShipping, setSelectedPurchaseForShipping] = useState(null);
  const [showEditShippingModal, setShowEditShippingModal] = useState(false);

  const [selectedPOForNotification, setSelectedPOForNotification] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const [selectedPOForStatus, setSelectedPOForStatus] = useState(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState('ordered');
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSupplierId, setFilterSupplierId] = useState('all');
  const [filterLocationId, setFilterLocationId] = useState('all');
  const [filterShippingStatus, setFilterShippingStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (rowId, event) => {
    event.stopPropagation();
    if (openActionId === rowId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(rowId);
    }
  };

  useEffect(() => { fetchPurchaseOrders(); }, [currentPage, perPage]);

  useEffect(() => {
    const timer = setTimeout(() => { setCurrentPage(1); fetchPurchaseOrders(); }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const locResp = await apiService.getLocations();
        setLocations(locResp?.data || locResp || []);
      } catch (e) { /* console.warn('Failed to fetch locations:', e); */ }
      try {
        const supResp = await apiService.getContacts();
        const contactList = supResp?.data || supResp || [];
        setSuppliers(contactList.filter(c => c.type === 'supplier' || c.type === 'both'));
      } catch (e) { /* console.warn('Failed to fetch suppliers:', e); */ }
    };
    fetchFilterData();
  }, []);

  const fetchPurchaseOrders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, per_page: perPage, search: searchTerm };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSupplierId !== 'all') params.supplier_id = filterSupplierId;
      if (filterLocationId !== 'all') params.location_id = filterLocationId;
      if (filterShippingStatus !== 'all') params.shipping_status = filterShippingStatus;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;

      const response = await apiService.getPurchaseOrders(params);
      if (response.success) {
        setPurchaseOrders(response.data || []);
        setTotal(response.total || 0);
        setLastPage(response.last_page || 1);
      } else {
        setError(response.message || 'Failed to load purchase orders');
        setPurchaseOrders([]);
      }
    } catch (err) {
      /* console.error('Failed to load purchase orders', err); */
      setError(err.message || 'Failed to load purchase orders');
      setPurchaseOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setOpenActionId(null);
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    try {
      const res = await apiService.deletePurchaseOrder(id);
      if (res && res.success !== false) {
        toast.success('Purchase order deleted successfully');
        fetchPurchaseOrders();
      } else {
        toast.error(res?.message || 'Failed to delete purchase order');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase order');
    }
  };

  const handleOpenStatusModal = (po) => {
    if (!canEdit) return;
    setSelectedPOForStatus(po);
    setSelectedStatusValue(po.status || 'ordered');
    setShowEditStatusModal(true);
  };

  const handleSaveStatus = async () => {
    if (!selectedPOForStatus) return;
    setIsUpdatingStatus(true);
    try {
      const res = await apiService.updatePurchaseOrderStatus(selectedPOForStatus.id, selectedStatusValue);
      if (res && res.success !== false) {
        toast.success('Status updated successfully');
        fetchPurchaseOrders();
        setShowEditStatusModal(false);
      } else {
        toast.error(res?.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handlePrintPO = async (po) => {
    setOpenActionId(null);
    try {
      if (po.purchase_lines && po.purchase_lines.length > 0) {
        printPurchaseOrder(po);
      } else {
        const res = await apiService.getPurchaseOrder(po.id);
        printPurchaseOrder(res.data || res || po);
      }
    } catch (err) {
      printPurchaseOrder(po);
    }
  };

  const handleViewPO = async (po) => {
    setOpenActionId(null);
    setShowViewModal(true);
    setIsLoadingViewModal(true);
    try {
      const res = await apiService.getPurchaseOrder(po.id);
      setSelectedPurchaseForView(res.data || res);
    } catch (err) {
      setSelectedPurchaseForView(po);
    } finally {
      setIsLoadingViewModal(false);
    }
  };

  const handleDownloadPdf = async (po) => {
    setOpenActionId(null);
    try {
      toast.info('Downloading PDF...');
      await apiService.downloadPurchaseOrderPdf(po.id);
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handleEditShipping = (po) => {
    setOpenActionId(null);
    setSelectedPurchaseForShipping(po);
    setShowEditShippingModal(true);
  };

  const handleSendNotification = (po) => {
    setOpenActionId(null);
    setSelectedPOForNotification(po);
    setShowNotificationModal(true);
  };

  const applyFilters = () => { setCurrentPage(1); fetchPurchaseOrders(); setShowFilters(false); };

  const resetFilters = () => {
    setFilterStatus('all');
    setFilterSupplierId('all');
    setFilterLocationId('all');
    setFilterShippingStatus('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchPurchaseOrders();
    setShowFilters(false);
  };

  const handleExport = (format) => {
    if (!purchaseOrders || purchaseOrders.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }
    const headers = ['PO No', 'Date', 'Supplier', 'Location', 'Status', 'Grand Total', 'Shipping Status', 'Added By'];
    const rows = purchaseOrders.map(r => [
      r.po_no || r.ref_no || '', r.date || r.transaction_date || '', r.supplier_name || r.contact?.name || '', r.location_name || r.location?.name || '',
      r.status || '', parseFloat(r.final_total || 0).toFixed(2), r.shipping_status || '', r.added_by || ''
    ]);
    const title = 'Purchase Orders'; const filename = 'purchase_orders';
    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();
    setShowExportDropdown(false);
  };

  const renderStatusBadge = (statusText, onClick = null) => {
    if (!statusText || statusText === 'N/A') {
      return (
        <span className="payment-status-badge" style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.26rem 0.72rem',
          borderRadius: '20px',
          fontSize: '0.78rem',
          fontWeight: '700',
          textTransform: 'uppercase',
          color: '#ffffff',
          letterSpacing: '0.2px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #94a3b8, #64748b)'
        }}>
          N/A
        </span>
      );
    }

    const s = String(statusText).toLowerCase();
    let bg = 'linear-gradient(135deg, #0284c7, #0369a1)';

    if (s === 'delivered' || s === 'completed' || s === 'approved' || s === 'received' || s === 'paid') {
      bg = 'linear-gradient(135deg, #10b981, #059669)';
    } else if (s === 'due' || s === 'partial' || s === 'partially_received' || s === 'pending') {
      bg = 'linear-gradient(135deg, #f59e0b, #d97706)';
    } else if (s === 'overdue' || s === 'cancelled' || s === 'rejected') {
      bg = 'linear-gradient(135deg, #ef4444, #dc2626)';
    } else if (s === 'ordered' || s === 'shipped' || s === 'processing' || s === 'new' || s === 'draft') {
      bg = 'linear-gradient(135deg, #0284c7, #0369a1)';
    }

    const formattedText = String(statusText).toUpperCase();

    return (
      <span
        onClick={onClick}
        className="payment-status-badge"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.26rem 0.72rem',
          borderRadius: '20px',
          fontSize: '0.78rem',
          fontWeight: '700',
          textTransform: 'uppercase',
          color: '#ffffff',
          letterSpacing: '0.2px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          background: bg,
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
        {formattedText}
      </span>
    );
  };

  const renderShippingStatusBadge = (statusText) => {
    if (!statusText || statusText === 'N/A') {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.26rem 0.72rem',
          borderRadius: '20px',
          fontSize: '0.78rem',
          fontWeight: '700',
          textTransform: 'uppercase',
          color: '#64748b',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
        }}>
          N/A
        </span>
      );
    }

    const s = String(statusText).toLowerCase();
    let textColor = '#334155';
    let borderColor = '#cbd5e1';

    if (s === 'delivered' || s === 'completed') {
      textColor = '#059669';
      borderColor = '#a7f3d0';
    } else if (s === 'shipped') {
      textColor = '#0284c7';
      borderColor = '#bae6fd';
    } else if (s === 'ordered' || s === 'pending') {
      textColor = '#475569';
      borderColor = '#e2e8f0';
    }

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.26rem 0.72rem',
        borderRadius: '20px',
        fontSize: '0.78rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        color: textColor,
        background: '#ffffff',
        border: `1px solid ${borderColor}`,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
      }}>
        {String(statusText).toUpperCase()}
      </span>
    );
  };

  return (
    <div className="products-container">
      <div className="main-card">
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input" />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => navigate('/purchases/order/add')}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {error && <div className="error-banner" style={{ padding: '1rem', color: '#ef4444' }}>{error}</div>}

        {isLoading ? (
          <div className="loading-sales">
            <h3>⏳ Loading Purchase Orders...</h3>
            <p>Fetching purchase orders data from server...</p>
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="no-sales">
            <h3>📊 No Purchase Orders Found</h3>
            <p>No purchase orders have been recorded yet.</p>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table fit-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Date</th>
                  <th>PO No</th>
                  <th>Supplier</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Shipping Status</th>
                  <th>Grand Total</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map(po => (
                  <tr key={po.id}>
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="action-dropdown-container">
                            <button className="action-btn-trigger" onClick={(e) => toggleDropdown(po.id, e)}>
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openActionId === po.id && ReactDOM.createPortal(
                              <div
                                className="action-dropdown-menu"
                                style={{
                                  position: 'fixed',
                                  top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                  bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                  left: `${dropdownPosition.left}px`,
                                  maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                  zIndex: 9999999
                                }}
                              >
                                 {canView && (
                                   <>
                                     <button className="dropdown-item" onClick={() => handleViewPO(po)}>👁️ View</button>
                                     <button className="dropdown-item" onClick={() => handlePrintPO(po)}>🖨️ Print</button>
                                     <button className="dropdown-item" onClick={() => handleDownloadPdf(po)}>📄 Download pdf</button>
                                   </>
                                 )}
                                {canEdit && (
                                  <button className="dropdown-item" onClick={() => { setOpenActionId(null); navigate(`/purchases/order/${po.id}/edit`); }}>✏️ Edit</button>
                                )}
                                {canDelete && (
                                  <button className="dropdown-item delete-item" onClick={() => handleDelete(po.id)}>🗑️ Delete</button>
                                )}
                                {canEditShipping && (
                                  <button className="dropdown-item" onClick={() => handleEditShipping(po)}>🚚 Edit Shipping</button>
                                )}
                                {canView && (
                                  <button className="dropdown-item" onClick={() => handleSendNotification(po)}>✉️ Send Notification</button>
                                )}
                              </div>,
                              document.body
                            )}
                          </div>
                        </td>
                    <td>{po.transaction_date || 'N/A'}</td>
                    <td>{po.ref_no || 'N/A'}</td>
                    <td>{po.supplier_business_name || po.name || 'N/A'}</td>
                    <td>{po.location_name || 'N/A'}</td>
                    <td>{renderStatusBadge(po.status, canEdit ? () => handleOpenStatusModal(po) : null)}</td>
                    <td>${parseFloat(po.final_total || 0).toFixed(2)}</td>
                    <td>{renderShippingStatusBadge(po.shipping_status)}</td>
                    <td>{po.added_by || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
              <div>
                Showing {total === 0 ? 0 : ((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                  Page {currentPage} of {lastPage}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                  disabled={currentPage >= lastPage}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Status:</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="ordered">Ordered</option>
                    <option value="received">Received</option>
                    <option value="partial">Partial</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Shipping Status:</label>
                  <select value={filterShippingStatus} onChange={(e) => setFilterShippingStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="not_shipped">Not Shipped</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="returned">Returned</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Supplier:</label>
                  <select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)}>
                    <option value="all">All</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name || s.supplier_business_name || `ID: ${s.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Location:</label>
                  <select value={filterLocationId} onChange={(e) => setFilterLocationId(e.target.value)}>
                    <option value="all">All</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name || `ID: ${l.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Start Date:</label>
                  <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>End Date:</label>
                  <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
                </div>
              </div>
              <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="control-btn-secondary" onClick={resetFilters}>Reset</button>
                <button className="control-btn-primary" onClick={applyFilters}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Purchase Order Modal */}
      {showViewModal && (
        <ViewPurchaseModal
          purchase={selectedPurchaseForView}
          isLoading={isLoadingViewModal}
          onClose={() => { setShowViewModal(false); setSelectedPurchaseForView(null); }}
          onPrint={handlePrintPO}
        />
      )}

      {/* Edit Shipping Modal */}
      {showEditShippingModal && selectedPurchaseForShipping && (
        <EditShippingModal
          sale={selectedPurchaseForShipping}
          onClose={() => { setShowEditShippingModal(false); setSelectedPurchaseForShipping(null); }}
          onUpdate={fetchPurchaseOrders}
        />
      )}

      {/* Edit Status Modal */}
      {showEditStatusModal && (
        <div className="modal-overlay" onClick={() => setShowEditStatusModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '90%', borderRadius: '8px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0, color: '#111827' }}>Edit status</h3>
              <button onClick={() => setShowEditStatusModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>
            <div className="modal-content" style={{ padding: '1.25rem' }}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.4rem', color: '#374151' }}>Status:</label>
                <select
                  value={selectedStatusValue}
                  onChange={(e) => setSelectedStatusValue(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem', outline: 'none' }}
                >
                  <option value="ordered">Ordered</option>
                  <option value="partial">Partial</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveStatus}
                disabled={isUpdatingStatus}
                style={{ backgroundColor: '#6366f1', color: 'white', border: 'none', padding: '0.45rem 1.25rem', borderRadius: '4px', fontWeight: '500', cursor: 'pointer', fontSize: '0.875rem' }}
              >
                {isUpdatingStatus ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && selectedPOForNotification && (
        <SendNotificationModal
          po={selectedPOForNotification}
          onClose={() => { setShowNotificationModal(false); setSelectedPOForNotification(null); }}
        />
      )}
    </div>
  </div>
);
};

export default PurchaseOrder;
